import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DIFFICULTY, STATUS, verdictStyle, DIMS } from "@/lib/format";

export const StatusBadge = ({ status, className = "" }) => {
  const s = STATUS[status] || STATUS.queued;
  return (
    <span className={`chip ${className}`} style={{ color: s.color, borderColor: `${s.color}44` }} data-testid={`status-badge-${status}`}>
      {(status === "running" || status === "judging") && <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: s.color }} /><span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: s.color }} /></span>}
      {s.label}
    </span>
  );
};

export const VerdictBadge = ({ verdict, size = "md" }) => {
  if (!verdict) return <span className="chip text-white/40">Pending</span>;
  const v = verdictStyle(verdict);
  return <span className={`chip ${size === "lg" ? "text-sm px-4 py-1.5" : ""}`} style={{ color: v.color, background: v.bg, borderColor: `${v.color}55` }} data-testid="verdict-badge">{verdict}</span>;
};

export const PersonaAvatar = ({ persona, size = 40 }) => (
  <span className="grid shrink-0 place-items-center rounded-full font-heading font-bold text-ink" aria-hidden="true"
    style={{ width: size, height: size, fontSize: size * 0.36, background: `linear-gradient(135deg, ${DIFFICULTY[persona?.difficulty] || "#22D3EE"}, #8B5CF6)` }}>
    {persona?.avatar_initials || "?"}
  </span>
);

export const DifficultyChip = ({ level }) => (
  <span className="chip capitalize" style={{ color: DIFFICULTY[level], borderColor: `${DIFFICULTY[level]}44` }}>{level}</span>
);

export const Waveform = ({ active, color = "#22D3EE", bars = 14, height = 24 }) => (
  <span className="inline-flex items-center gap-[2px]" style={{ height }} aria-hidden="true">
    {Array.from({ length: bars }).map((_, i) => (
      <span key={i} className={`w-[3px] rounded-full ${active ? "wave-bar" : ""}`}
        style={{ height: "100%", background: color, opacity: active ? 0.9 : 0.25, animationDelay: `${(i % 5) * 0.12}s`, transform: active ? undefined : "scaleY(0.3)" }} />
    ))}
  </span>
);

export const TypingDots = ({ label = "typing" }) => (
  <span className="inline-flex items-center gap-1 text-xs text-white/50" aria-label={label}>
    {[0, 1, 2].map((i) => <span key={i} className="typing-dot h-1.5 w-1.5 rounded-full bg-cyan-300" style={{ animationDelay: `${i * 0.2}s` }} />)}
  </span>
);

export const Skeleton = ({ className = "" }) => <div className={`skeleton ${className}`} aria-hidden="true" />;

export const CountUp = ({ value, decimals = 0, suffix = "", className = "", duration = 1100 }) => {
  const target = Number(value) || 0;
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start;
    const tick = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      setN(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return <span className={className}>{n.toFixed(decimals)}{suffix}</span>;
};

export const ScoringPopover = ({ scoring }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-white transition-colors" aria-label="How scoring works" data-testid="scoring-info-button">
        <Info size={13} /> How scoring works
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-80 panel p-4 text-sm" data-testid="scoring-popover">
      <p className="eyebrow mb-2">Weighted score</p>
      <ul className="space-y-1 text-white/75">
        {DIMS.map(([k, label]) => (
          <li key={k} className="flex justify-between"><span>{label}</span><span className="font-mono text-white/90">{Math.round((scoring?.weights?.[k] ?? 0) * 100)}%</span></li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-white/60 leading-relaxed">
        Each judge dimension is 0–10; the weighted sum ×10 gives a call score out of 100. Readiness is the average across all calls.
        <span className="block mt-2 text-bad/90 font-medium">Compliance block:</span> any compliance breach caps that call at {scoring?.compliance_cap ?? 49} and forces the whole test to <b>Not Ready</b>, regardless of average.
        <span className="block mt-2">Pilot Ready ≥ {scoring?.pilot_ready ?? 75} · Needs Work ≥ {scoring?.needs_work ?? 50}.</span>
      </p>
    </PopoverContent>
  </Popover>
);
